// What the inbox shows about the customer behind a ticket, under the
// conversation: their orders and their store transactions, each with a
// search and sortable columns of its own, so the agent sees the whole
// history while answering.
// Registered with the customer-support ticket page at boot (routes.js).
import React, { useCallback, useEffect, useState } from "react"
import DataTable from "aldoo-ra/DataTable"
import { registerCustomerPanel } from "aldoo-ra/Admin/CustomerSupport/references"
import { StoreTransactionAPI } from "aldoo-ra/Admin/Store/Transactions/api"
import { ListOrders } from "../AdminShared/api"
import { Chip, Thumb, Empty, money, dateTime, timeAgo, orderState } from "../AdminShared/ui"
import OrderPage from "../AdminVendors/OrderPage"
import { placedAt } from "aldoo-ra/StoreOrders/ui"

const norm = (v) => String(v || "").toLocaleLowerCase("bg")
const has = (hay, query) => {
  const words = norm(query).split(/\s+/).filter(Boolean)
  const text = hay.map(norm).join(" ")
  return words.every((w) => text.includes(w))
}
const termOf = (search) => Object.values(search || {}).find((v) => typeof v === "string" && v.trim()) || ""

/**
 * The table's page over rows already in memory: the DataTable asks its
 * apiMethod for a page with the search and the sort its header controls
 * hold, so a local pager gives it sortable columns and paging for free.
 * `text(row)` is what the search matches, `keys` the value of a sortable
 * column, and `sort` the default order.
 */
const usePager = (rows, { text, keys }) =>
  useCallback(
    async ({ page, itemsPerPage, search, sort }) => {
      if (!rows) return { data: [], total: 0 }
      const term = termOf(search)
      let list = term ? rows.filter((row) => has(text(row), term)) : rows
      const key = sort?.field && keys[sort.field]
      if (key) {
        const dir = sort.order === "desc" ? -1 : 1
        list = [...list].sort((a, b) => {
          const x = key(a)
          const y = key(b)
          if (x === y) return 0
          if (x === undefined || x === null || x === "") return 1
          if (y === undefined || y === null || y === "") return -1
          return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "bg")) * dir
        })
      }
      const start = (page - 1) * itemsPerPage
      return { data: list.slice(start, start + itemsPerPage), total: list.length }
    },
    [rows, text, keys]
  )

const When = ({ value }) => (
  <span className="block whitespace-nowrap">
    <span className="block text-admin_text">{dateTime(value)}</span>
    <span className="block text-xs text-admin_text_secondary">{timeAgo(value)}</span>
  </span>
)

const Head = ({ title, count, onClose }) => (
  <div className="flex items-center justify-between mb-3">
    <span className="font-semibold text-admin_text">{title}{count !== undefined ? ` (${count})` : ""}</span>
    <button type="button" onClick={onClose} className="text-xs text-admin_text_secondary hover:text-admin_text">Close</button>
  </div>
)

const TABLE = {
  variant: "admin",
  itemIdField: "_id",
  itemsPerPage: 10,
  mobileCards: false,
  styleOverrides: { container: "flex flex-col", row: "h-auto text-admin_text text-sm font-normal" },
}

const ORDER_TEXT = (o) => [o.orderId, o.orderStatus, orderState(o).label, ...(o.cart || []).flatMap((i) => [i.name, i.pid])]
const ORDER_KEYS = {
  orderId: (o) => o.orderId || String(o._id).slice(-8).toUpperCase(),
  completedAt: (o) => new Date(placedAt(o) || 0).getTime(),
  amount: (o) => Number(o.final?.net ?? o.amount) || 0,
  orderStatus: (o) => orderState(o).label,
}
const ORDER_COLUMNS = [
  {
    id: "orderId",
    title: "Order",
    sortable: true,
    render: (orderId, order) => {
      const items = order.cart || []
      return (
        <div className="min-w-0" data-testid="customer-order-row">
          <div className="font-medium text-admin_text">#{orderId || String(order._id).slice(-8).toUpperCase()}</div>
          <div className="mt-1 flex items-center gap-2 overflow-hidden">
            {items.slice(0, 4).map((item, i) => (
              <Thumb key={`${item.pid}-${i}`} src={item.thumbnail} name={item.name || item.pid} className="w-7 h-7 rounded shrink-0" />
            ))}
            <span className="min-w-0 truncate text-xs text-admin_text_secondary">{items.map((i) => i.name || i.pid).join(", ")}</span>
          </div>
        </div>
      )
    },
  },
  { id: "completedAt", title: "Placed", sortable: true, headerClassName: "w-[170px]", render: (value, order) => <When value={placedAt(order)} /> },
  // what the order came to after its refunds (the api's `final`, aldoo-na/store/refunds netOf)
  { id: "amount", title: "Total", sortable: true, headerClassName: "w-[110px] text-right", render: (amount, order) => <span className="block text-right tabular-nums text-admin_text">{money(order.final?.net ?? amount, order.currency)}</span> },
  {
    id: "orderStatus",
    title: "Status",
    sortable: true,
    headerClassName: "w-[130px]",
    render: (_, order) => {
      const state = orderState(order)
      return <Chip className={state.className}>{state.label}</Chip>
    },
  },
]

/** The customer's orders: what they bought, when, and where each stands. */
function OrdersPanel({ customer, onClose }) {
  const [orders, setOrders] = useState(null)
  const [open, setOpen] = useState("")
  useEffect(() => {
    if (!customer.email) return setOrders([])
    setOrders(null)
    // the search of the orders api matches the email: every order of this address
    ListOrders({ search: customer.email, page: 1, items_per_page: 200, order_by: "-completedAt" })
      .then((res) => setOrders((res?.data || []).filter((o) => (o.email || "").toLowerCase() === customer.email)))
      .catch(() => setOrders([]))
  }, [customer.email])
  const loadPage = usePager(orders, { text: ORDER_TEXT, keys: ORDER_KEYS })
  return (
    <div data-testid="customer-orders">
      <Head title="Orders" count={orders?.length} onClose={onClose} />
      {orders === null && <div className="text-sm text-admin_text_secondary">Loading…</div>}
      {orders && (
        <DataTable
          {...TABLE}
          apiMethod={loadPage}
          refreshKey={`${customer.email}:${orders.length}`}
          sort={{ field: "completedAt", order: "desc" }}
          searchField="orderId"
          searchPlaceholder="Order number, product, status…"
          onRowClick={(order) => setOpen(order.orderId || order._id)}
          renders={ORDER_COLUMNS}
          noData={<Empty>No orders for this address</Empty>}
        />
      )}
      {open && <OrderPage orderId={open} onClose={() => setOpen("")} onChanged={() => {}} />}
    </div>
  )
}

const amountOf = (t) => t.price_snapshot?.final_amount ?? t.price_snapshot?.amount ?? 0
const TX_TEXT = (t) => [t.product_id, t.type, t.platform, t.receipt_id, t.price_snapshot?.promo_code]
const TX_KEYS = {
  product_id: (t) => t.product_id || "",
  type: (t) => t.type || "",
  platform: (t) => t.platform || "",
  created: (t) => new Date(t.created || 0).getTime(),
  amount: (t) => Number(amountOf(t)) || 0,
}
const TX_COLUMNS = [
  { id: "product_id", title: "Product", sortable: true, render: (value) => <span className="block min-w-0 truncate text-admin_text" data-testid="customer-transaction-row">{value || "—"}</span> },
  { id: "type", title: "Type", sortable: true, headerClassName: "w-[120px]", render: (value, t) => <span className="text-xs uppercase tracking-wide text-admin_text_secondary">{value}{t.is_renew ? " · renewal" : ""}</span> },
  { id: "platform", title: "Platform", sortable: true, headerClassName: "w-[100px]", render: (value) => <span className="text-xs text-admin_text_secondary">{value}</span> },
  { id: "created", title: "When", sortable: true, headerClassName: "w-[170px]", render: (value) => <When value={value} /> },
  {
    id: "amount",
    title: "Amount",
    sortable: true,
    headerClassName: "w-[110px] text-right",
    render: (_, t) => <span className={`block text-right tabular-nums ${t.type === "refund" ? "text-admin_error" : "text-admin_text"}`}>{money(amountOf(t), t.price_snapshot?.currency)}</span>,
  },
]

/** The customer's store ledger: every purchase, subscription, refund. */
function TransactionsPanel({ customer, onClose }) {
  const [rows, setRows] = useState(null)
  useEffect(() => {
    if (customer.resolving) return
    if (!customer.user?._id) return setRows([])
    setRows(null)
    StoreTransactionAPI.GetTransactions({ match: { user: customer.user._id }, sort: { created: -1 }, itemsPerPage: 200, page: 1 })
      .then((res) => setRows(res?.data || res?.result?.data || []))
      .catch(() => setRows([]))
  }, [customer.user?._id, customer.resolving])
  const loadPage = usePager(rows, { text: TX_TEXT, keys: TX_KEYS })
  return (
    <div data-testid="customer-transactions">
      <Head title="Transactions" count={rows?.length} onClose={onClose} />
      {customer.resolving && <div className="text-sm text-admin_text_secondary">Looking up the account…</div>}
      {!customer.resolving && !customer.user && <div className="text-sm text-admin_text_secondary">No platform account for {customer.email}</div>}
      {customer.user && rows === null && <div className="text-sm text-admin_text_secondary">Loading…</div>}
      {customer.user && rows && (
        <DataTable
          {...TABLE}
          apiMethod={loadPage}
          refreshKey={`${customer.user._id}:${rows.length}`}
          sort={{ field: "created", order: "desc" }}
          searchField="product_id"
          searchPlaceholder="Product, type, platform, receipt…"
          renders={TX_COLUMNS}
          noData={<Empty>No transactions</Empty>}
        />
      )}
    </div>
  )
}

registerCustomerPanel({ id: "orders", label: "Orders", Panel: OrdersPanel })
registerCustomerPanel({ id: "transactions", label: "Transactions", Panel: TransactionsPanel })
